import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { teamsAPI } from '../api/api';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const ApplicationManagement = () => {
  const [applications, setApplications] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selectedApp, setSelectedApp] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    loadApplications();
    loadTeams();
  }, [filter]);

  const loadApplications = async () => {
    try {
      const token = localStorage.getItem('token');
      const url = filter === 'all' 
        ? `${API_URL}/applications`
        : `${API_URL}/applications?status=${filter}`;
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setApplications(response.data);
    } catch (error) {
      console.error('Error loading applications:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTeams = async () => {
    try {
      const response = await teamsAPI.getAll();
      setTeams(response.data);
    } catch (error) {
      console.error('Error loading teams:', error);
    }
  };

  const updateStatus = async (id, status, adminNotes = '') => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_URL}/applications/${id}/status`,
        { status, adminNotes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessage({ type: 'success', text: `Application ${status}` });
      loadApplications();
      setSelectedApp(null);
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.error || 'Failed to update application' });
    }
  };

  const sendInvite = async (id, teamId = null) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/applications/${id}/send-invite`,
        { teamId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessage({ 
        type: 'success', 
        text: `Invite code generated: ${response.data.inviteCode.code}. Send this code to the applicant!` 
      });
      loadApplications();
      setSelectedApp(null);
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.error || 'Failed to send invite' });
    }
  };

  const deleteApplication = async (id) => {
    if (!window.confirm('Are you sure you want to delete this application?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/applications/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessage({ type: 'success', text: 'Application deleted' });
      loadApplications();
      setSelectedApp(null);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to delete application' });
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800',
      approved: 'bg-green-100 dark:bg-green-900/30 text-green-800',
      rejected: 'bg-red-100 dark:bg-red-900/30 text-red-800',
      invited: 'bg-blue-100 dark:bg-blue-900/30 text-blue-800'
    };
    return styles[status] || 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200';
  };

  const getPlatformIcon = (platform) => {
    return platform === 'Xbox' ? '🎮' : '🎯';
  };

  const filteredApplications = applications;
  const stats = {
    total: applications.length,
    pending: applications.filter(a => a.status === 'pending').length,
    approved: applications.filter(a => a.status === 'approved').length,
    invited: applications.filter(a => a.status === 'invited').length,
    rejected: applications.filter(a => a.status === 'rejected').length
  };

  if (loading) {
    return <div className="text-center py-8">Loading applications...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Team Applications</h2>
      </div>

      {message.text && (
        <div className={`p-4 rounded ${
          message.type === 'success' ? 'bg-green-50 border border-green-200 text-green-700 dark:text-green-400' : 'bg-red-50 border border-red-200 text-red-700 dark:text-red-400'
        }`}>
          <button onClick={() => setMessage({ type: '', text: '' })} className="float-right text-xl">&times;</button>
          {message.text}
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow border-l-4 border-gray-400">
          <div className="text-2xl font-bold">{stats.total}</div>
          <div className="text-sm text-gray-600 dark:text-gray-400">Total</div>
        </div>
        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow border-l-4 border-yellow-400">
          <div className="text-2xl font-bold">{stats.pending}</div>
          <div className="text-sm text-gray-600 dark:text-gray-400">Pending</div>
        </div>
        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow border-l-4 border-green-400 dark:border-green-700">
          <div className="text-2xl font-bold">{stats.approved}</div>
          <div className="text-sm text-gray-600 dark:text-gray-400">Approved</div>
        </div>
        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow border-l-4 border-blue-400">
          <div className="text-2xl font-bold">{stats.invited}</div>
          <div className="text-sm text-gray-600 dark:text-gray-400">Invited</div>
        </div>
        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow border-l-4 border-red-400 dark:border-red-700">
          <div className="text-2xl font-bold">{stats.rejected}</div>
          <div className="text-sm text-gray-600 dark:text-gray-400">Rejected</div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
        <div className="flex gap-2 flex-wrap">
          {['all', 'pending', 'approved', 'invited', 'rejected'].map(status => (
            <button
              key={status}
              onClick={() => setFilter(status)}
              className={`px-4 py-2 rounded font-semibold ${
                filter === status
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-300'
              }`}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Applications List */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        {filteredApplications.length === 0 ? (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            No applications found
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Applicant</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Gamertag / Platform</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Preferred Team</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Applied</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {filteredApplications.map(app => (
                  <tr key={app.id} className="hover:bg-gray-50 dark:bg-gray-800">
                    <td className="px-6 py-4">
                      <div className="font-semibold text-gray-900 dark:text-gray-100">{app.firstName} {app.lastName}</div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">{app.email}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="font-semibold">{app.gamertag}</div>
                      <div className="text-sm">
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-700">
                          {getPlatformIcon(app.platform)} {app.platform}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {app.preferredTeam ? (
                        <div className="text-sm">
                          <div className="font-semibold">{app.preferredTeam.abbreviation}</div>
                          <div className="text-gray-500 dark:text-gray-400">{app.preferredTeam.city}</div>
                        </div>
                      ) : (
                        <span className="text-gray-400 text-sm">No preference</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs font-semibold rounded ${getStatusBadge(app.status)}`}>
                        {app.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                      {new Date(app.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4">
                      <button
                        onClick={() => setSelectedApp(app)}
                        className="text-blue-600 dark:text-blue-400 hover:underline text-sm font-semibold"
                      >
                        View Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Application Detail Modal */}
      {selectedApp && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-2xl font-bold">Application Details</h3>
                <button onClick={() => setSelectedApp(null)} className="text-gray-400 hover:text-gray-600 dark:text-gray-400 text-2xl">
                  &times;
                </button>
              </div>

              <div className="space-y-4">
                {/* Applicant Info */}
                <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
                  <h4 className="font-semibold mb-2">Applicant Information</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div><span className="text-gray-600 dark:text-gray-400">Name:</span> {selectedApp.firstName} {selectedApp.lastName}</div>
                    <div><span className="text-gray-600 dark:text-gray-400">Email:</span> {selectedApp.email}</div>
                    <div><span className="text-gray-600 dark:text-gray-400">Gamertag:</span> {selectedApp.gamertag}</div>
                    <div><span className="text-gray-600 dark:text-gray-400">Platform:</span> {getPlatformIcon(selectedApp.platform)} {selectedApp.platform}</div>
                  </div>
                </div>

                {/* Team Preference */}
                {selectedApp.preferredTeam && (
                  <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                    <h4 className="font-semibold mb-2">Preferred Team</h4>
                    <div className="text-sm">
                      {selectedApp.preferredTeam.city} {selectedApp.preferredTeam.name} ({selectedApp.preferredTeam.abbreviation})
                    </div>
                  </div>
                )}

                {/* Additional Info */}
                {(selectedApp.experience || selectedApp.reason || selectedApp.availability) && (
                  <div className="space-y-3">
                    {selectedApp.experience && (
                      <div>
                        <h4 className="font-semibold mb-1">NBA 2K Experience</h4>
                        <p className="text-sm text-gray-700 dark:text-gray-300">{selectedApp.experience}</p>
                      </div>
                    )}
                    {selectedApp.reason && (
                      <div>
                        <h4 className="font-semibold mb-1">Why Join?</h4>
                        <p className="text-sm text-gray-700 dark:text-gray-300">{selectedApp.reason}</p>
                      </div>
                    )}
                    {selectedApp.availability && (
                      <div>
                        <h4 className="font-semibold mb-1">Availability</h4>
                        <p className="text-sm text-gray-700 dark:text-gray-300">{selectedApp.availability}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Status Actions */}
                <div className="border-t pt-4">
                  <h4 className="font-semibold mb-3">Actions</h4>
                  <div className="space-y-3">
                    {selectedApp.status === 'pending' && (
                      <>
                        <button
                          onClick={() => updateStatus(selectedApp.id, 'approved')}
                          className="w-full bg-green-600 text-white py-2 rounded hover:bg-green-700"
                        >
                          ✓ Approve Application
                        </button>
                        <button
                          onClick={() => updateStatus(selectedApp.id, 'rejected')}
                          className="w-full bg-red-600 text-white py-2 rounded hover:bg-red-700"
                        >
                          ✗ Reject Application
                        </button>
                      </>
                    )}

                    {(selectedApp.status === 'approved' || selectedApp.status === 'pending') && !selectedApp.inviteCodeId && (
                      <div>
                        <label className="block text-sm font-medium mb-1">Assign Team (Optional)</label>
                        <select
                          id="team-select"
                          className="w-full px-3 py-2 border rounded mb-2"
                          defaultValue={selectedApp.preferredTeamId || ''}
                        >
                          <option value="">No team assignment</option>
                          {teams.filter(t => !t.owner).map(team => (
                            <option key={team.id} value={team.id}>
                              {team.city} {team.name} ({team.abbreviation})
                            </option>
                          ))}
                        </select>
                        <button
                          onClick={() => {
                            const teamId = document.getElementById('team-select').value;
                            sendInvite(selectedApp.id, teamId || null);
                          }}
                          className="w-full bg-blue-600 text-white py-2 rounded hover:bg-blue-700"
                        >
                          📧 Generate & Send Invite Code
                        </button>
                      </div>
                    )}

                    {selectedApp.inviteCode && (
                      <div className="bg-green-50 p-4 rounded border border-green-200">
                        <div className="font-semibold text-green-900">Invite Code Sent</div>
                        <div className="text-2xl font-mono font-bold text-green-700 dark:text-green-400 my-2">
                          {selectedApp.inviteCode.code}
                        </div>
                        <div className="text-sm text-green-800">
                          Send this code to: {selectedApp.email}
                        </div>
                      </div>
                    )}

                    <button
                      onClick={() => deleteApplication(selectedApp.id)}
                      className="w-full bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 py-2 rounded hover:bg-gray-300"
                    >
                      🗑️ Delete Application
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApplicationManagement;
